/**
 * Structural subset of a Datadog RUM error event, typed locally so the filter
 * stays testable without constructing a full SDK event.
 */
export type FilterableRumEvent = {
  type: string;
  error?: {
    message?: string;
    stack?: string;
    source?: string;
  };
};

/**
 * Console output from vendors we load but do not control. RUM records every
 * `console.error` as an error, so their logging lands next to ours:
 *
 * - `Amplitude Logger [Error]:` — the analytics SDK reporting its own blocked
 *   or failed uploads (ad blockers, flaky networks).
 * - `Failed to load Amplitude experiments:` — our own log line in
 *   `analytics.ts`; the failure is already reported, sampled, via `addError`.
 * - `Error: Request timeout after N milliseconds` — the experiment client's
 *   `fetch` catching its own flag-request timeout and passing it to
 *   `console.error`, which prefixes the error's name. The uncaught form of the
 *   same timeout is `AMPLITUDE_FETCH_TIMEOUT`.
 * - `Usercentrics [` / `[CMP] Your browser` — the consent banner's SDK logging
 *   its fetch failures and unsupported-browser warning.
 */
const VENDOR_CONSOLE_MESSAGES = [
  /^Amplitude Logger \[Error\]: /,
  /^Failed to load Amplitude experiments: /,
  /^Error: Request timeout after \d+ milliseconds$/,
  /^Usercentrics \[/,
  /^\[CMP\] Your browser /,
];

/**
 * The browser telling us it ignored a scroll-lock library's `preventDefault`
 * on a touchmove it could not cancel. Harmless, and not an exception.
 */
const TOUCHMOVE_INTERVENTION =
  /^intervention: Ignored attempt to cancel a touchmove/;

/**
 * Thrown by Amplitude's own HTTP client once `fetchTimeoutMillis` elapses.
 */
const AMPLITUDE_FETCH_TIMEOUT = /^Request timeout after \d+ milliseconds$/;

/**
 * Sources that are not our bundle: in-app browser bridges (`iabjs://`),
 * browser extensions, and Safari's masked extension URLs. Nothing we ship can
 * raise from these, so an error with such a frame in its stack is never ours
 * to fix.
 */
const THIRD_PARTY_SOURCE_IN_STACK =
  /(iabjs|chrome-extension|moz-extension|safari-extension|safari-web-extension|webkit-masked-url):\/\//;

/**
 * Messages raised entirely outside our code. Each one was verified against a
 * real event (in Sentry, before RUM) before being listed here:
 *
 * - `Error invoking postMessage` / `window.webkit.messageHandlers` / `454:
 *   Handling is disabled` — the Facebook and Instagram in-app browser bridges
 *   talking to a native host that has already gone away.
 * - `_AutofillCallbackHandler` — iOS WebView's autofill hook, absent until the
 *   host app injects it.
 * - `runtime.sendMessage` / `Object Not Found Matching Id` — extensions and
 *   Outlook's link scanner reaching into a tab that has already navigated.
 * - `Database deleted by request of the user` — the visitor cleared site data
 *   while an IndexedDB handle was open. Expected, and nothing breaks.
 * - `Script error.` — the browser masking an exception from a script served
 *   by another origin: no message, file or stack survive. Our bundle is
 *   same-origin, so its errors always arrive in full; these come from vendor
 *   tags and from scripts the browser injects itself (most of them Firefox on
 *   iOS), and there is nothing in them to fix.
 */
const THIRD_PARTY_MESSAGES = [
  /^Error invoking postMessage: /,
  /evaluating 'window\.webkit\.messageHandlers'/,
  /^454: Handling is disabled$/,
  /_AutofillCallbackHandler/,
  /^Invalid call to runtime\.sendMessage\(\)/,
  /Object Not Found Matching Id:/,
  /Database deleted by request of the user/,
  /^(Uncaught ")?Script error\.("?)$/,
];

/**
 * Every RUM error `beforeSend` should throw away. Drop only verified vendor
 * noise, so a real error from our own bundle still reports.
 */
export const isDroppableRumError = (event: FilterableRumEvent): boolean => {
  if (event.type !== "error") {
    return false;
  }

  const message = event.error?.message ?? "";
  const stack = event.error?.stack ?? "";

  return (
    AMPLITUDE_FETCH_TIMEOUT.test(message) ||
    THIRD_PARTY_MESSAGES.some((pattern) => pattern.test(message)) ||
    THIRD_PARTY_SOURCE_IN_STACK.test(stack) ||
    (event.error?.source === "console" &&
      VENDOR_CONSOLE_MESSAGES.some((pattern) => pattern.test(message))) ||
    (event.error?.source === "report" && TOUCHMOVE_INTERVENTION.test(message))
  );
};
